import { supabase } from './supabase'
import { config } from './config'
import type {
  UserProfile,
  UserProfileUpdate,
  UserPreference,
  UserPreferenceUpdate,
  Podcast,
  PodcastInsert,
  PodcastUpdate,
  PodcastEpisode,
  EpisodeMetric,
  ConnectedPlatform,
  CsvUpload,
  Guest,
  GuestInsert,
  GuestUpdate,
  GuestAppearance,
  ShowFollowerTrend,
  ShowGeoTrend,
  EpisodePerformanceWindow,
} from '../types/database'
import type { EpisodeAnalytics, RetentionCurveResponse, TranscriptResponse, BulkTranscriptPreviewResponse, BulkTranscriptConfirmResponse } from '../types/episode-analytics'
import type { DashboardOverview, RecentEpisode } from '../types/dashboard'
import type {
  GuestScore,
  GuestWithDetails,
  GuestNetwork,
  GuestDiscoveryResult,
  GuestRecommendationWithGuest,
  ReinviteStatus,
  GuestExternalAppearance,
  GuestRecentNews,
  PitchAngleResult,
  CoverageGapResult,
  BriefingPack,
  ComparableShowsResult,
  GuestYouTubePresence,
  GuestNicheActivity,
  GuestAlert,
  GuestResearchResult,
  FeedGuest,
  GuestSuggestion,
  GuestDashboardSummary,
} from '../types/guest-intel'
import type { SignalTopic, EpisodePrediction } from '../types/database'
import type { GrowthOpportunity } from '../types/trend-intelligence'
import type { RetentionProfileWithEpisode, RetentionAnalysis } from '../types/retention'
import type { VoiceProfileRow, EpisodeVoiceMetricRow, VoiceAnalysisCombined } from '../types/voice-identity'
import type { SponsorMatchesResult, AdPlacementResult, CpmProjectionResult, DealWithSponsor } from '../types/monetization'
import type { HealthScoreResult } from '../types/dashboard'
import type { ClipCandidate, ClipCandidateUpdate, CompetitorShow, CompetitorShowInsert, PlannedEpisode, PlannedEpisodeClipProposal, DescriptClipJob } from '../types/database'
import type { CompetitorSummary, ContentGapResult } from '../types/competitive'
import type { SegmentEstimatesResult, ChurnRiskResult, GeoDistributionData } from '../types/audience'
import type { LaunchEvent, EpisodeRoadmap, EpisodeRoadmapUpdate } from '../types/database'
import type { ClipSocialRouting, SocialPlatform } from '../types/database'
import type { PageContext } from './pageContext'

// Module-level token cache updated by auth listener — avoids Web Locks contention
let _cachedAccessToken: string | null = null

supabase.auth.onAuthStateChange((_event, session) => {
  _cachedAccessToken = session?.access_token ?? null
})

// Seed from existing session on module load
supabase.auth.getSession().then(({ data: { session } }) => {
  _cachedAccessToken = session?.access_token ?? null
})

export async function getAuthHeader(): Promise<Record<string, string>> {
  if (_cachedAccessToken) return { Authorization: `Bearer ${_cachedAccessToken}` }
  // Fallback: read from session directly (may contend on lock, but only once)
  const { data: { session } } = await supabase.auth.getSession()
  if (session?.access_token) {
    _cachedAccessToken = session.access_token
    return { Authorization: `Bearer ${session.access_token}` }
  }
  return {}
}

// Typed 403 error for team-member scope-denial responses from the backend
// (`{error: "scope_denied", scope: "<key>"}`). Frontends render
// NotAccessibleCard when this throws.
export class ScopeDeniedError extends Error {
  readonly scope: string
  constructor(scope: string) {
    super(`scope_denied: ${scope}`)
    this.scope = scope
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeader()),
    ...options.headers,
  }

  const res = await fetch(`${config.apiBase}${path}`, {
    ...options,
    headers,
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    if (res.status === 401 && error?.error === 'team_access_revoked') {
      // Backend revoked this team member's access. Sign out so
      // AuthContext.onAuthStateChange clears session → ProtectedRoute
      // redirects to /auth (preventing an indefinite broken-UI state).
      await supabase.auth.signOut()
      throw new Error('team_access_revoked')
    }
    if (
      res.status === 403 &&
      error?.error === 'scope_denied' &&
      typeof error?.scope === 'string'
    ) {
      throw new ScopeDeniedError(error.scope)
    }
    throw new Error(error.message || `API error: ${res.status}`)
  }

  return res.json()
}

// ============================================================
// Profile
// ============================================================

export function getProfile(): Promise<UserProfile> {
  return apiFetch('/user/profile')
}

export function updateProfile(data: UserProfileUpdate): Promise<UserProfile> {
  return apiFetch('/user/profile', { method: 'PATCH', body: JSON.stringify(data) })
}

export function getPreferences(): Promise<UserPreference> {
  return apiFetch('/user/preferences')
}

export function updatePreferences(data: UserPreferenceUpdate): Promise<UserPreference> {
  return apiFetch('/user/preferences', { method: 'PATCH', body: JSON.stringify(data) })
}

// ============================================================
// Podcasts
// ============================================================

export function getPodcasts(): Promise<Podcast[]> {
  return apiFetch('/podcasts')
}

export function createPodcast(data: PodcastInsert): Promise<Podcast> {
  return apiFetch('/podcasts', { method: 'POST', body: JSON.stringify(data) })
}

export function getPodcast(id: string): Promise<Podcast> {
  return apiFetch(`/podcasts/${id}`)
}

export function updatePodcast(id: string, data: PodcastUpdate): Promise<Podcast> {
  return apiFetch(`/podcasts/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deletePodcast(id: string): Promise<void> {
  return apiFetch(`/podcasts/${id}`, { method: 'DELETE' })
}

// ============================================================
// Episodes
// ============================================================

export function getEpisodes(podcastId: string): Promise<PodcastEpisode[]> {
  return apiFetch(`/podcasts/${podcastId}/episodes`)
}

export function getEpisode(id: string): Promise<PodcastEpisode> {
  return apiFetch(`/episodes/${id}`)
}

export function getEpisodeMetrics(id: string): Promise<EpisodeMetric[]> {
  return apiFetch(`/episodes/${id}/metrics`)
}

export function uploadTranscript(
  episodeId: string,
  source: 'spotify' | 'apple_podcasts' | 'youtube' | 'manual',
  content: string
): Promise<{ success: boolean; segmentCount: number; charCount: number; source: string }> {
  return apiFetch(`/episodes/${episodeId}/transcript`, {
    method: 'POST',
    body: JSON.stringify({ source, content }),
  })
}

export function getEpisodeTranscript(episodeId: string): Promise<TranscriptResponse> {
  return apiFetch(`/episodes/${episodeId}/transcript`)
}

// ── Bulk transcript upload (Phase 30) ──

export async function bulkTranscriptPreview(
  files: File[],
  podcastId: string
): Promise<BulkTranscriptPreviewResponse> {
  const formData = new FormData()
  files.forEach((f) => {
    formData.append('files', f)
    // Send lastModified per file for Tier 3 matching
    formData.append(`lastModified_${f.name}`, String(f.lastModified))
  })
  formData.append('podcastId', podcastId)

  const authHeaders = await getAuthHeader()

  const res = await fetch(`${config.apiBase}/ingest/bulk-transcripts/preview`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new Error(error.message || `Preview error: ${res.status}`)
  }

  return res.json()
}

export async function bulkTranscriptConfirm(
  tempSessionId: string,
  matches: Array<{ filename: string; episodeId: string }>,
  overwrite?: boolean
): Promise<BulkTranscriptConfirmResponse> {
  return apiFetch('/ingest/bulk-transcripts/confirm', {
    method: 'POST',
    body: JSON.stringify({ tempSessionId, matches, overwrite }),
  })
}

// ============================================================
// Social Analytics
// ============================================================

export interface SocialOverview {
  platforms: Array<{
    connectionId: string
    platform: string
    followers: number
    totalViews: number
    snapshotDate: string | null
  }>
  totalFollowers: number
  totalViews30d: number
  topContent: Array<{
    contentId: string
    platform: string
    title: string
    permalink: string
    contentType: string
    views: number
    likes: number
    comments: number
    shares: number
    engagementRate: number
  }>
}

export interface SocialSnapshot {
  id: string
  connection_id: string
  platform: string
  snapshot_date: string
  followers: number | null
  following: number | null
  total_views: number | null
  total_likes: number | null
  video_count: number | null
  subscriber_count: number | null
}

export interface SocialContentItem {
  id: string
  platform: string
  platform_content_id: string
  content_type: string
  title: string | null
  description: string | null
  permalink: string | null
  published_at: string | null
  duration_seconds: number | null
  is_grasppod_clip: boolean
  latestMetrics: {
    views: number
    likes: number
    comments: number
    shares: number
    saves: number
    engagement_rate: number
    watch_time_minutes: number | null
  } | null
}

export interface SocialDemographicsData {
  age_breakdown: Record<string, number> | null
  gender_breakdown: Record<string, number> | null
  country_breakdown: Record<string, number> | null
  city_breakdown: Record<string, number> | null
}

export function getSocialOverview(): Promise<SocialOverview> {
  return apiFetch('/social/overview')
}

export function getSocialSnapshots(connectionId: string, days = 30): Promise<SocialSnapshot[]> {
  return apiFetch(`/social/snapshots?connectionId=${connectionId}&days=${days}`)
}

export function getSocialContent(platform?: string, limit = 50, offset = 0): Promise<{ items: SocialContentItem[]; total: number }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (platform) params.set('platform', platform)
  return apiFetch(`/social/content?${params}`)
}

export function getSocialContentMetrics(contentId: string, days = 30): Promise<Record<string, unknown>[]> {
  return apiFetch(`/social/content/${contentId}/metrics?days=${days}`)
}

export function getSocialDemographics(connectionId: string): Promise<SocialDemographicsData | null> {
  return apiFetch(`/social/demographics?connectionId=${connectionId}`)
}

// Server returns 202 Accepted immediately — sync runs in background. Poll
// connected_platforms.sync_status to detect completion.
export function syncSocialPlatform(connectionId: string): Promise<{ status?: 'processing'; message: string }> {
  return apiFetch(`/social/sync/${connectionId}`, { method: 'POST' })
}

// YTDF-6: manual YouTube resync — bypasses ingestion.ts:460 HOSTING_PLATFORMS
// whitelist via dedicated POST /api/youtube/resync endpoint. Uses raw fetch
// instead of apiFetch<T> so callers can distinguish 202 / 409 / 404 cleanly
// (apiFetch throws a generic Error on any non-2xx and loses status code).
export interface ResyncYouTubeResponse {
  sync_status: 'syncing' | 'synced' | 'idle' | 'error'
  sync_started_at: string | null
  connection_id: string
}

export class ResyncAlreadySyncingError extends Error {
  constructor(
    public readonly syncStartedAt: string | null,
    public readonly connectionId: string,
  ) {
    super('already_syncing')
    this.name = 'ResyncAlreadySyncingError'
  }
}

export class NoYouTubeConnectionError extends Error {
  constructor() {
    super('no_youtube_connection')
    this.name = 'NoYouTubeConnectionError'
  }
}

export async function resyncYouTube(): Promise<ResyncYouTubeResponse> {
  const res = await fetch(`${config.apiBase}/youtube/resync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getAuthHeader()),
    },
  })

  if (res.status === 202) {
    return (await res.json()) as ResyncYouTubeResponse
  }
  if (res.status === 409) {
    const body = await res.json().catch(() => ({} as { sync_started_at?: string; connection_id?: string }))
    throw new ResyncAlreadySyncingError(body.sync_started_at ?? null, body.connection_id ?? '')
  }
  if (res.status === 404) {
    throw new NoYouTubeConnectionError()
  }
  const body = await res.json().catch(() => ({ error: `http_${res.status}` } as { error: string }))
  throw new Error(`resync_failed: ${body.error || `HTTP ${res.status}`}`)
}

// ============================================================
// Platforms
// ============================================================

export function connectPlatform(data: {
  platform: string
  podcastId: string
  credentials: Record<string, string>
}): Promise<ConnectedPlatform> {
  return apiFetch('/platforms/connect', { method: 'POST', body: JSON.stringify(data) })
}

export function disconnectPlatform(id: string): Promise<void> {
  return apiFetch(`/platforms/${id}/disconnect`, { method: 'DELETE' })
}

export function syncPlatform(id: string): Promise<void> {
  return apiFetch(`/platforms/${id}/sync`, { method: 'POST' })
}

export function getPlatformStatus(): Promise<ConnectedPlatform[]> {
  return apiFetch('/platforms/status')
}

// ============================================================
// OAuth Authorization URL
// ============================================================

// Constructs the full-browser-redirect URL for social OAuth flows.
// This is NOT called via apiFetch -- the caller uses window.location.href.
// The access token is passed as a query param because browser navigations
// cannot send Authorization headers.
export function getOAuthAuthorizeUrl(platform: string, podcastId: string): string {
  const base = import.meta.env.VITE_API_BASE || '/api'
  const token = _cachedAccessToken || ''
  return `${base}/auth/oauth/${platform}/authorize?podcastId=${podcastId}&access_token=${encodeURIComponent(token)}`
}

// ============================================================
// AdSense (YTDF-7, D27)
// ============================================================

export interface AdSenseAccount {
  id: string
  account_name: string
  display_name: string | null
  time_zone: string | null
  currency_code: string | null
  premium: boolean
  state: 'READY' | 'NEEDS_ATTENTION' | 'CLOSED' | null
  pending_tasks: string[] | null
  is_active: boolean
  last_sync_at: string | null
  sync_status: 'idle' | 'syncing' | 'synced' | 'error'
  sync_error: string | null
  created_at: string
}

// AdSense OAuth start — mirrors getOAuthAuthorizeUrl but targets the dedicated
// /adsense/authorize endpoint (AdSense bypasses the generic platform dispatcher
// per plan §3.2). No podcastId param — AdSense is user-scoped, not per-podcast.
export function getAdSenseAuthorizeUrl(): string {
  const base = import.meta.env.VITE_API_BASE || '/api'
  const token = _cachedAccessToken || ''
  return `${base}/auth/adsense/authorize?access_token=${encodeURIComponent(token)}`
}

export function connectAdSense(): void {
  window.location.href = getAdSenseAuthorizeUrl()
}

export function getAdSenseAccounts(): Promise<AdSenseAccount[]> {
  return apiFetch('/adsense/accounts')
}

export function disconnectAdSense(accountId: string): Promise<void> {
  return apiFetch(`/adsense/accounts/${accountId}`, { method: 'DELETE' })
}

// ============================================================
// YouTube Content Owner mode (YTDF-14)
// ============================================================

export interface ContentOwnerStatus {
  content_owner_name: string | null
  monetary_scope_granted: boolean
  has_revenue_rows: boolean
}

export function getYouTubeContentOwner(): Promise<ContentOwnerStatus> {
  return apiFetch('/youtube/content-owner')
}

export function setYouTubeContentOwner(
  content_owner_name: string,
): Promise<{ status: 'ok'; content_owner_name: string }> {
  return apiFetch('/youtube/content-owner', {
    method: 'POST',
    body: JSON.stringify({ content_owner_name }),
  })
}

export function unsetYouTubeContentOwner(): Promise<{ status: 'removed' }> {
  return apiFetch('/youtube/content-owner', { method: 'DELETE' })
}

export function getYouTubeMonetaryAuthorizeUrl(): string {
  const base = import.meta.env.VITE_API_BASE || '/api'
  const token = _cachedAccessToken || ''
  return `${base}/auth/youtube/monetary-authorize?access_token=${encodeURIComponent(token)}`
}

export function upgradeYouTubeMonetary(): void {
  window.location.href = getYouTubeMonetaryAuthorizeUrl()
}

// ============================================================
// Monetization — YouTube revenue (Phase 10)
// ============================================================

export interface MonetizationOverview {
  adsenseConnected: boolean
  adsenseState: string | null
  adsenseCurrency: string | null
  adsenseLastSyncAt: string | null
  contentOwnerConnected: boolean
}

export interface YouTubeRevenueOverview {
  totalRevenue30d: number
  totalRevenue90d: number
  totalRevenueYtd: number
  currency: string | null
  lastSyncAt: string | null
}

export interface YouTubeRevenueTrendRow {
  date: string
  estimatedEarnings: number
  impressions: number
  clicks: number
  impressionsRpm: number
  currency: string | null
}

export interface YouTubeRevenueCountry {
  countryCode: string
  earnings: number
  share: number
  rpm: number
  currency: string | null
}

export interface YouTubeRevenuePlatform {
  code: string
  earnings: number
  share: number
  impressions: number
  currency: string | null
}

export interface YouTubeCpmTrendRow {
  date: string
  impressionsRpm: number
  costPerClick: number
  currency: string | null
}

export type RevenueSource = 'estimated' | 'actual'

export interface YouTubeRevenueByVideo {
  videoId: string
  title: string | null
  revenue: number
  source: RevenueSource
}

export interface YouTubeAdPerformance {
  impressions: number
  clicks: number
  ctr: number
  viewability: number
  adRequests: number
  matchedAdRequests: number
  coverage: number
  currency: string | null
}

// All paths hit `/api/monetization/...` externally; apiFetch already
// prepends config.apiBase (which resolves to `/api`), so hook paths
// start at `/monetization/...`.

export function getMonetizationOverview(): Promise<MonetizationOverview> {
  return apiFetch('/monetization/overview')
}

export function getYouTubeRevenueOverview(): Promise<YouTubeRevenueOverview> {
  return apiFetch('/monetization/youtube/revenue-overview')
}

export function getYouTubeRevenueTrend(
  days: number,
): Promise<{ rows: YouTubeRevenueTrendRow[] }> {
  return apiFetch(`/monetization/youtube/revenue-trend?days=${days}`)
}

export function getYouTubeRevenueByCountry(
  days: number,
  limit?: number,
): Promise<{ countries: YouTubeRevenueCountry[]; totalEarnings: number }> {
  const params = new URLSearchParams({ days: String(days) })
  if (limit !== undefined) params.set('limit', String(limit))
  return apiFetch(`/monetization/youtube/revenue-by-country?${params.toString()}`)
}

export function getYouTubeRevenueByPlatform(
  days: number,
): Promise<{ platforms: YouTubeRevenuePlatform[] }> {
  return apiFetch(`/monetization/youtube/revenue-by-platform?days=${days}`)
}

export function getYouTubeCpmTrend(
  days: number,
): Promise<{ rows: YouTubeCpmTrendRow[] }> {
  return apiFetch(`/monetization/youtube/cpm-trend?days=${days}`)
}

export function getYouTubeRevenueByVideo(
  days: number,
  limit?: number,
): Promise<{ videos: YouTubeRevenueByVideo[]; source: RevenueSource }> {
  const params = new URLSearchParams({ days: String(days) })
  if (limit !== undefined) params.set('limit', String(limit))
  return apiFetch(`/monetization/youtube/revenue-by-video?${params.toString()}`)
}

export function getYouTubeAdPerformance(
  days: number,
): Promise<YouTubeAdPerformance> {
  return apiFetch(`/monetization/youtube/ad-performance?days=${days}`)
}

// ============================================================
// CSV Upload
// ============================================================

export async function uploadCSV(file: File, podcastId: string, platform: string): Promise<CsvUpload> {
  const formData = new FormData()
  formData.append('files', file)
  formData.append('podcastId', podcastId)
  formData.append('platform', platform)

  const authHeaders = await getAuthHeader()

  const res = await fetch(`${config.apiBase}/ingest/csv-upload`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new Error(error.message || `Upload error: ${res.status}`)
  }

  const data = await res.json()
  // Server returns single record or array; normalize to single for backward compat
  return Array.isArray(data) ? data[0] : data
}

export function getCSVUploads(): Promise<CsvUpload[]> {
  return apiFetch('/ingest/csv-uploads')
}

// ============================================================
// Dashboard
// ============================================================

export function getHealthScore(podcastId: string): Promise<HealthScoreResult> {
  return apiFetch(`/dashboard/health-score?podcastId=${podcastId}`)
}

export function getDashboardOverview(podcastId: string): Promise<DashboardOverview> {
  return apiFetch(`/dashboard/overview?podcastId=${podcastId}`)
}

export function getRecentEpisodes(podcastId: string): Promise<RecentEpisode[]> {
  return apiFetch(`/dashboard/recent-episodes?podcastId=${podcastId}`)
}

// ============================================================
// Guest Intelligence
// ============================================================

export function getGuests(): Promise<Guest[]> {
  return apiFetch('/guests')
}

export function createGuest(data: Omit<GuestInsert, 'user_id'>): Promise<Guest> {
  return apiFetch('/guests', { method: 'POST', body: JSON.stringify(data) })
}

export function getGuest(id: string): Promise<GuestWithDetails> {
  return apiFetch(`/guests/${id}`)
}

export function updateGuest(id: string, data: GuestUpdate): Promise<Guest> {
  return apiFetch(`/guests/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteGuest(id: string): Promise<void> {
  return apiFetch(`/guests/${id}`, { method: 'DELETE' })
}

export function getGuestAppearances(id: string): Promise<GuestAppearance[]> {
  return apiFetch(`/guests/${id}/appearances`)
}

export function getGuestRecommendations(podcastId: string): Promise<GuestRecommendationWithGuest[]> {
  return apiFetch(`/guests/recommendations?podcastId=${podcastId}`)
}

export function scoreGuest(id: string, podcastId: string): Promise<GuestScore> {
  return apiFetch(`/guests/${id}/score?podcastId=${podcastId}`, { method: 'POST' })
}

export function discoverGuests(topic: string): Promise<GuestDiscoveryResult[]> {
  return apiFetch(`/guests/discover?topic=${encodeURIComponent(topic)}`)
}

export function getGuestNetwork(podcastId: string): Promise<GuestNetwork> {
  return apiFetch(`/guests/network?podcastId=${podcastId}`)
}

export function getReinviteReadyGuests(): Promise<ReinviteStatus[]> {
  return apiFetch('/guests/reinvite-ready')
}

// GI-1 endpoints

export function getGuestExternalAppearances(
  guestId: string,
  page = 1,
  limit = 20,
): Promise<GuestExternalAppearance[]> {
  return apiFetch(`/guests/${guestId}/appearances/external?page=${page}&limit=${limit}`)
}

export function getGuestRecentNews(
  guestId: string,
  refresh = false,
): Promise<GuestRecentNews[]> {
  const qs = refresh ? '?refresh=true' : ''
  return apiFetch(`/guests/${guestId}/recent-news${qs}`)
}

export function getGuestPitchAngle(
  guestId: string,
  podcastId: string,
  refresh = false,
): Promise<PitchAngleResult> {
  const params = new URLSearchParams({ podcastId })
  if (refresh) params.set('refresh', 'true')
  return apiFetch(`/guests/${guestId}/pitch-angle?${params}`)
}

export function getCoverageGaps(podcastId: string): Promise<CoverageGapResult[]> {
  return apiFetch(`/guests/coverage-gaps?podcastId=${encodeURIComponent(podcastId)}`)
}

export function getGuestBriefing(
  guestId: string,
  podcastId: string,
  refresh = false,
): Promise<BriefingPack> {
  const params = new URLSearchParams({ podcastId })
  if (refresh) params.set('refresh', 'true')
  return apiFetch(`/guests/${guestId}/briefing?${params}`)
}

export function getComparableShows(
  guestId: string,
  podcastId: string,
  refresh = false,
): Promise<ComparableShowsResult> {
  const params = new URLSearchParams({ podcastId })
  if (refresh) params.set('refresh', 'true')
  return apiFetch(`/guests/${guestId}/comparable-shows?${params}`)
}

export function getHotInNicheFeed(category: string, limit = 20): Promise<FeedGuest[]> {
  return apiFetch(`/guests/feed/hot-in-niche?category=${encodeURIComponent(category)}&limit=${limit}`)
}

export function getRisingGuestsFeed(category?: string, limit = 20): Promise<FeedGuest[]> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (category) params.set('category', category)
  return apiFetch(`/guests/feed/rising?${params}`)
}

// GI-2 endpoints

export async function getGuestYouTube(guestId: string): Promise<GuestYouTubePresence | null> {
  try {
    return await apiFetch<GuestYouTubePresence>(`/guests/${guestId}/youtube`)
  } catch {
    return null
  }
}

export function getGuestNicheActivity(guestId: string): Promise<GuestNicheActivity[]> {
  return apiFetch(`/guests/${guestId}/niche-activity`)
}

export function searchGuestResearch(
  query: string,
  niche?: string,
  limit = 10,
): Promise<GuestResearchResult[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) })
  if (niche) params.set('niche', niche)
  return apiFetch(`/guests/research?${params}`)
}

// GI-3 endpoints

export function getCompetitorWatchFeed(limit = 20, dismissed = false): Promise<GuestAlert[]> {
  return apiFetch(`/competitive/feed/competitor-watch?limit=${limit}&dismissed=${dismissed}`)
}

export async function dismissAlert(alertId: string): Promise<void> {
  await apiFetch(`/competitive/alerts/${alertId}/dismiss`, { method: 'PATCH' })
}

// GI-5 endpoints

export function getGuestSuggestions(
  topic: string,
  podcastId: string,
  limit = 3,
): Promise<GuestSuggestion[]> {
  return apiFetch(`/guests/suggestions?topic=${encodeURIComponent(topic)}&podcastId=${podcastId}&limit=${limit}`)
}

// B-K2.3 — CC corpus suggestion endpoints
import type { CCCorpusSuggestion } from '../types/guest-intel'

export interface CCCorpusSuggestionsResponse {
  suggestions: CCCorpusSuggestion[]
  hint?: string
}

export function getCCCorpusSuggestions(limit = 10): Promise<CCCorpusSuggestionsResponse> {
  return apiFetch(`/guests/research/cc-suggestions?limit=${limit}`)
}

export function addCCCorpusGuest(
  guestName: string,
  mentionCount?: number,
): Promise<{ guest: { id: string; name: string } }> {
  return apiFetch('/guests/research/cc-suggestions/add', {
    method: 'POST',
    body: JSON.stringify({ guestName, mentionCount }),
  })
}

export function getGuestDashboardSummary(podcastId: string): Promise<GuestDashboardSummary> {
  return apiFetch(`/guests/dashboard-summary?podcastId=${podcastId}`)
}

// ============================================================
// SignalCast
// ============================================================

export interface PredictionInput {
  podcastId: string
  topic: string
  guestId?: string
  proposedReleaseDay?: string
  proposedReleaseHour?: number
  titleVariants?: string[]
}

export interface PredictionOutput {
  predictedDownloadsLow: number
  predictedDownloadsMid: number
  predictedDownloadsHigh: number
  predictedCompletionRate: number
  viralProbability: number
  optimalReleaseDay: string
  optimalReleaseHour: number
  signalFactors: Record<string, number>
  predictionConfidence: 'low' | 'medium' | 'high'
}

export interface SignalResult {
  source: string
  score: number
  raw: unknown
  fetchedAt: string
}

export interface ReleaseWindowResult {
  optimalDay: string
  optimalHour: number
  heatmap: { day: string; hour: number; intensity: number }[]
  isHoliday: boolean
  holidayWarning?: string
  // PARTIAL engine flag: true when no published_at history exists and
  // optimalDay/optimalHour fall back to the hardcoded 'Tuesday 6am' default.
  degraded: boolean
}

export type PredictionEngineResult =
  | { hasData: true; confidence: 'low' | 'medium' | 'high'; data: PredictionOutput & { id: string } }
  | { hasData: false; reason: string }

export interface PredictResponse {
  prediction: PredictionEngineResult
  signals: Record<string, SignalResult>
  releaseWindow: ReleaseWindowResult
}

export interface TitleScore {
  title: string
  score: number
  factors: {
    length: number
    keywords: number
    curiosity: number
    specificity: number
    emotional: number
  }
}

export function getSignalTopics(podcastId: string): Promise<SignalTopic[]> {
  return apiFetch(`/signalcast/topics?podcastId=${podcastId}`)
}

export function predictEpisode(input: PredictionInput): Promise<PredictResponse> {
  return apiFetch('/signalcast/predict', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function getEpisodePrediction(episodeId: string): Promise<EpisodePrediction> {
  return apiFetch(`/signalcast/predictions/${episodeId}`)
}

export function scoreTitles(titles: string[], topic: string): Promise<TitleScore[]> {
  return apiFetch('/signalcast/title-ab', {
    method: 'POST',
    body: JSON.stringify({ titles, topic }),
  })
}

export function getReleaseWindow(podcastId: string): Promise<ReleaseWindowResult> {
  return apiFetch(`/signalcast/release-window?podcastId=${podcastId}`)
}

// ============================================================
// Retention DNA
// ============================================================

export function getRetentionProfile(episodeId: string): Promise<RetentionProfileWithEpisode> {
  return apiFetch(`/retention/${episodeId}`)
}

export function analyzeRetention(episodeId: string): Promise<RetentionAnalysis> {
  return apiFetch(`/retention/analyze/${episodeId}`, { method: 'POST' })
}

// ============================================================
// Voice Identity
// ============================================================

export function getVoiceProfile(podcastId: string): Promise<VoiceProfileRow> {
  return apiFetch(`/voice/profile/${podcastId}`)
}

export interface VoiceAnalyzeResponse {
  // Synchronous success
  analysis?: VoiceAnalysisCombined['voiceMetrics']
  profile?: VoiceAnalysisCombined['profile']
  drift?: VoiceAnalysisCombined['drift']
  // Background (202) response
  status?: 'processing'
  message?: string
}

export function analyzeVoice(episodeId: string): Promise<VoiceAnalyzeResponse> {
  return apiFetch(`/voice/analyze/${episodeId}`, { method: 'POST' })
}

export function getVoiceMetrics(episodeId: string): Promise<EpisodeVoiceMetricRow> {
  return apiFetch(`/voice/metrics/${episodeId}`)
}

export function getVoiceMetricsBatch(podcastId: string): Promise<Record<string, EpisodeVoiceMetricRow>> {
  return apiFetch(`/voice/metrics-batch/${podcastId}`)
}

// Map of episodeId -> true for episodes that have a transcript (text or segments).
// Kept separate from getEpisodes() because including transcript content in the
// main list blows past response-size limits on shows with many episodes.
export function getTranscriptStatus(podcastId: string): Promise<Record<string, boolean>> {
  return apiFetch(`/voice/transcript-status/${podcastId}`)
}

export interface BulkAnalyzeResponse {
  queued: number
  total?: number
  message: string
}

export function bulkAnalyzeTranscribed(podcastId: string): Promise<BulkAnalyzeResponse> {
  return apiFetch(`/voice/bulk-analyze/${podcastId}`, { method: 'POST' })
}

// ============================================================
// Monetization
// ============================================================

export function getSponsorsWithMatches(podcastId: string): Promise<SponsorMatchesResult> {
  return apiFetch(`/monetization/sponsors?podcastId=${podcastId}`)
}

export function createSponsor(data: {
  name: string
  industry?: string
  website_url?: string
  contact_email?: string
  target_demographics?: Record<string, unknown>
  brand_keywords?: string[]
}): Promise<unknown> {
  return apiFetch('/monetization/sponsors', { method: 'POST', body: JSON.stringify(data) })
}

export function getDeals(podcastId?: string): Promise<DealWithSponsor[]> {
  const query = podcastId ? `?podcastId=${podcastId}` : ''
  return apiFetch(`/monetization/deals${query}`)
}

export function createDeal(data: Record<string, unknown>): Promise<unknown> {
  return apiFetch('/monetization/deals', { method: 'POST', body: JSON.stringify(data) })
}

export function updateDeal(id: string, data: Record<string, unknown>): Promise<unknown> {
  return apiFetch(`/monetization/deals/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function getCpmProjection(podcastId: string): Promise<CpmProjectionResult> {
  return apiFetch(`/monetization/cpm-projection/${podcastId}`)
}

export function getAdPlacements(episodeId: string): Promise<AdPlacementResult> {
  return apiFetch(`/monetization/ad-placement/${episodeId}`)
}

// ============================================================
// Clip Intelligence
// ============================================================

export function getClips(episodeId: string): Promise<ClipCandidate[]> {
  return apiFetch(`/clips/${episodeId}`)
}

export function detectClips(episodeId: string): Promise<{ clips: ClipCandidate[]; message?: string }> {
  return apiFetch(`/clips/detect/${episodeId}`, { method: 'POST' })
}

export function updateClip(clipId: string, data: ClipCandidateUpdate): Promise<ClipCandidate> {
  return apiFetch(`/clips/${clipId}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function exportClip(clipId: string, data: { platform: string }): Promise<ClipCandidate> {
  return apiFetch(`/clips/${clipId}/export`, { method: 'POST', body: JSON.stringify(data) })
}

// ============================================================
// Competitive Radar
// ============================================================

export function getCompetitorShows(podcastId: string): Promise<CompetitorSummary[]> {
  return apiFetch(`/competitive/shows?podcastId=${podcastId}`)
}

export type AddCompetitorShowResponse = CompetitorShow & {
  enrichmentResult?: unknown
  enrichmentError?: string
}

export function addCompetitorShow(
  data: Omit<CompetitorShowInsert, 'user_id'>,
  opts?: { enrich?: boolean },
): Promise<AddCompetitorShowResponse> {
  const path = opts?.enrich ? '/competitive/shows?enrich=true' : '/competitive/shows'
  return apiFetch(path, { method: 'POST', body: JSON.stringify(data) })
}

export function removeCompetitorShow(id: string): Promise<void> {
  return apiFetch(`/competitive/shows/${id}`, { method: 'DELETE' })
}

export function getCompetitorMetrics(competitorId: string): Promise<unknown> {
  return apiFetch(`/competitive/metrics/${competitorId}`)
}

export function getContentGaps(podcastId: string): Promise<ContentGapResult[]> {
  return apiFetch(`/competitive/gaps?podcastId=${podcastId}`)
}

export function validateCompetitorRss(
  rssUrl: string,
): Promise<{ valid: boolean; podcastTitle?: string; episodeCount?: number; error?: string }> {
  return apiFetch('/competitive/admin/validate-rss', {
    method: 'POST',
    body: JSON.stringify({ rssUrl }),
  })
}

export interface PodcastSuggestion {
  rssUrl: string
  title: string
  publisher: string
  artworkUrl: string
  appleId: string | null
  podcastIndexId: number | null
  episodeCount: number | null
  lastUpdateTimeMs: number | null
  // Cross-system cache fields (migration 076):
  podcastGuid: string | null
  hostingPlatform: string | null
}

export function searchCompetitorPodcasts(
  q: string,
): Promise<{ results: PodcastSuggestion[]; source?: string }> {
  return apiFetch('/competitive/admin/search-podcasts', {
    method: 'POST',
    body: JSON.stringify({ q }),
  })
}

export interface NicheGap {
  topic: string
  opportunityScore: number
  phase: string | null
  velocity: number | null
}

export function getCompetitiveNicheGaps(podcastId: string): Promise<NicheGap[]> {
  return apiFetch(`/competitive/niche-gaps?podcastId=${podcastId}`)
}

export function getCompetitiveQuickTake(
  podcastId: string,
): Promise<{ markdown: string; generatedAt: string }> {
  return apiFetch(`/competitive/quick-take?podcastId=${podcastId}`)
}

// W5 Q14: user-triggered refresh of personalized trend signals.
// Server rate-limits to 1/5min/user via in-memory Map and returns 429
// (Retry-After) on hit; consumer should display the rate-limit hint
// and avoid disabling the button permanently.
export interface PersonalizedTrendsRefreshResult {
  rows_upserted: number
  computed_at: string
  podcasts_processed: number
  topics_scored: number
  duration_ms: number
}

export function refreshPersonalizedTrends(): Promise<PersonalizedTrendsRefreshResult> {
  return apiFetch(`/trends/personalized/refresh`, { method: 'POST' })
}

export function getPersonalizedTrendsLastComputedAt(
  podcastId: string,
): Promise<{ computedAt: string | null }> {
  return apiFetch(`/trends/personalized/last-computed-at?podcastId=${podcastId}`)
}

// ============================================================
// Audience Intelligence
// ============================================================

export function getAudienceSegments(podcastId: string): Promise<SegmentEstimatesResult> {
  return apiFetch(`/audience/segments/${podcastId}`)
}

export function getChurnRisk(podcastId: string): Promise<ChurnRiskResult> {
  return apiFetch(`/audience/churn-risk/${podcastId}`)
}

export function getSuperfans(podcastId: string): Promise<unknown> {
  return apiFetch(`/audience/superfans/${podcastId}`)
}

export function getAudienceGeographic(podcastId: string): Promise<GeoDistributionData> {
  return apiFetch(`/audience/geographic/${podcastId}`)
}

// ============================================================
// Live Launch
// ============================================================

export function startLaunch(episodeId: string): Promise<LaunchEvent> {
  return apiFetch(`/launch/start/${episodeId}`, { method: 'POST' })
}

export function getActiveLaunch(): Promise<LaunchEvent | null> {
  return apiFetch('/launch/active')
}

export function getLaunchEvent(eventId: string): Promise<LaunchEvent> {
  return apiFetch(`/launch/event/${eventId}`)
}

export function endLaunch(eventId: string): Promise<LaunchEvent> {
  return apiFetch(`/launch/end/${eventId}`, { method: 'POST' })
}

export function getLaunchHistory(): Promise<LaunchEvent[]> {
  return apiFetch('/launch/history')
}

export function pulseLaunch(eventId: string): Promise<{
  launch: LaunchEvent
  timeSeries: {
    total: number
    byInterval: { interval: string; downloads_total: number; downloads_percent: number }[]
    publishedAt: string
  }
}> {
  return apiFetch(`/launch/pulse/${eventId}`, { method: 'POST' })
}

// ============================================================
// Episode Brain / Roadmap
// ============================================================

export interface RoadmapResponse {
  entries: EpisodeRoadmap[]
  balance: Record<string, number>
  // PARTIAL engine flag: true when any roadmap entry has a null topic OR the
  // performance predictor returned hasData:false for at least one entry.
  degraded: boolean
}

export function getRoadmap(podcastId: string): Promise<RoadmapResponse> {
  return apiFetch(`/roadmap/${podcastId}`)
}

export function generateRoadmap(podcastId: string): Promise<RoadmapResponse> {
  return apiFetch(`/roadmap/generate/${podcastId}`, { method: 'POST' })
}

export function updateRoadmapEntry(id: string, data: EpisodeRoadmapUpdate): Promise<EpisodeRoadmap> {
  return apiFetch(`/roadmap/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

// ============================================================
// Ivy Chat
// ============================================================

/** Phase 3: intent payload returned with every Ivy chat response. */
export type IvyIntent =
  | { type: 'conversation' }
  | {
      type: 'action'
      tier: 1 | 2
      action: string
      params: Record<string, unknown>
      confirmationMessage?: string
    }
  | { type: 'blocked'; action: string; reason: string }

export interface IvyChatResponse {
  content: string
  sessionId: string
  usage: { inputTokens: number; outputTokens: number }
  /** Phase 3: parsed intent block. Present when server has Phase 3 deployed. */
  intent?: IvyIntent
}

export interface IvySessionPreview {
  sessionId: string
  preview: string
  createdAt: string
}

export function sendIvyMessage(
  messages: { role: 'user' | 'assistant'; content: string }[],
  sessionId: string,
  podcastId: string,
  pageContext?: PageContext
): Promise<IvyChatResponse> {
  return apiFetch('/ivy/chat', {
    method: 'POST',
    body: JSON.stringify({ messages, sessionId, podcastId, pageContext }),
  })
}

export function getIvyContext(podcastId: string): Promise<{ context: string }> {
  return apiFetch(`/ivy/context?podcastId=${podcastId}`)
}

export function endIvySession(sessionId: string): Promise<{ message: string }> {
  return apiFetch('/ivy/session/end', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  })
}

export function getIvyHistory(): Promise<IvySessionPreview[]> {
  return apiFetch('/ivy/history')
}

/** Phase 3: write an audit row to job_logs for an executed intent action. */
export function logIvyAction(data: {
  action: string
  tier: 1 | 2
  params: Record<string, unknown>
  sessionId: string
  status: 'completed' | 'failed'
  errorMessage?: string
}): Promise<{ logged: boolean }> {
  return apiFetch('/ivy/log', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ============================================================
// Social Integration
// ============================================================

export interface UnifiedHealthResponse {
  // null when the underlying health-score engine returned hasData:false.
  podcastHealth: number | null
  socialHealth: number | null
  // null when podcastHealth is null (cross-engine propagation, never substituted).
  unifiedScore: number | null
  crossPlatformSynergy: number
  isLinked: boolean
  podcastHealthReason?: string
}

export interface SocialMetrics {
  views: number
  likes: number
  shares: number
  saves: number
  comments: number
}

export interface ClipRouteRequest {
  clipId: string
  targetPlatforms: SocialPlatform[]
  scheduledAt?: string
  caption?: string
  hashtags?: string[]
}

export function routeClipToSocial(data: ClipRouteRequest): Promise<ClipSocialRouting[]> {
  return apiFetch('/integration/route-clip', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function getRoutingQueue(): Promise<ClipSocialRouting[]> {
  return apiFetch('/integration/queue')
}

export function getClipRoutings(clipId: string): Promise<ClipSocialRouting[]> {
  return apiFetch(`/integration/routings/${clipId}`)
}

export function updateRoutingFeedback(routingId: string, metrics: SocialMetrics): Promise<ClipSocialRouting> {
  return apiFetch(`/integration/feedback/${routingId}`, {
    method: 'POST',
    body: JSON.stringify(metrics),
  })
}

export function getUnifiedHealth(podcastId: string): Promise<UnifiedHealthResponse> {
  return apiFetch(`/integration/unified-health?podcastId=${podcastId}`)
}

// ============================================================
// Integration -- Publishing (Phase 15D)
// ============================================================

// Publish a queued/scheduled clip routing immediately
export function publishRouting(routingId: string): Promise<ClipSocialRouting> {
  return apiFetch(`/integration/publish/${routingId}`, { method: 'POST' })
}

// ============================================================
// YouTube Intelligence (Phase 19)
// ============================================================

export interface YouTubeChannelPulse {
  connected: boolean
  connectionId?: string
  subscriberCount: number | null
  totalViews: number
  thirtyDayGrowth: { views: number; subs: number; subsGained: number; subsLost: number }
  watchTimeHours30d: number
  avgViewDuration: number
  publishingCadenceDays: number
  videoCount: number
  shortCount: number
}

export interface YouTubeDailyMetric {
  date: string
  views: number
  watchTimeMinutes: number
  subscribersGained: number
  subscribersLost: number
  likes: number
  comments: number
  shares: number
  avgViewDurationSeconds: number | null
}

export interface YouTubeContentItem {
  id: string
  platformContentId: string
  contentType: 'video' | 'short'
  title: string | null
  publishedAt: string | null
  durationSeconds: number | null
  tags: string[] | null
  category: string | null
  permalink: string | null
  metrics: {
    views: number
    likes: number
    comments: number
    shares: number
    watchTimeMinutes: number | null
    avgViewPercentage: number | null
    subscribersGained: number | null
    engagementRate: number | null
    endScreenCtr: number | null
    cardCtr: number | null
  } | null
  performanceTier: 'breakout' | 'above_avg' | 'average' | 'below_avg'
}

export interface YouTubeFormatSplit {
  videos: { count: number; avgViews: number; avgEngagement: number; totalWatchTime: number; avgRetention: number } | null
  shorts: { count: number; avgViews: number; avgEngagement: number; totalWatchTime: number; avgRetention: number } | null
}

export interface YouTubeAudience {
  demographics: { age: Record<string, number>; gender: Record<string, number> } | null
  geography: Record<string, number> | null
  device: Record<string, { views: number; watchTime: number }> | null
  os: Record<string, { views: number; watchTime: number }> | null
  playbackLocation: Record<string, { views: number; watchTime: number }> | null
  subscriberStatus: {
    subscribed: { views: number; watchTime: number; avgPct: number }
    unsubscribed: { views: number; watchTime: number; avgPct: number }
  } | null
}

export interface YouTubeTrafficSource {
  sourceType: string
  views: number
  watchTimeMinutes: number
  topDetails: Array<{ detail: string; views: number; watchTime: number }>
}

export interface YouTubeSearchTerm {
  term: string
  views: number
  watchTimeMinutes: number
}

export interface YouTubeRetentionOverview {
  avgRetention: number | null
  topPerformers: Array<{ contentId: string; title: string; avgRetention: number; curve: unknown[] }>
  bottomPerformers: Array<{ contentId: string; title: string; avgRetention: number; curve: unknown[] }>
}

export interface YouTubeLiveVideo {
  id: string
  title: string
  publishedAt: string
  contentType: 'video' | 'short'
  currentViews: number
  velocityPerHour: number
  channelAvgAtAge: number
  performanceMultiple: number
  isBreakout: boolean
}

export interface YouTubeContentFull {
  content: Record<string, unknown>
  metrics: Record<string, unknown> | null
  retention: unknown[] | null
  traffic: Array<{ source_type: string; source_detail: string | null; views: number; watch_time_minutes: number }>
}

export function getYouTubeChannelPulse(): Promise<YouTubeChannelPulse> {
  return apiFetch('/youtube/channel-pulse')
}

export function getYouTubeDailyMetrics(connectionId: string, days = 90): Promise<YouTubeDailyMetric[]> {
  return apiFetch(`/youtube/daily-metrics?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubeContent(params: { type?: string; sort?: string; limit?: number; offset?: number } = {}): Promise<{ items: YouTubeContentItem[]; total: number }> {
  const p = new URLSearchParams()
  if (params.type) p.set('type', params.type)
  if (params.sort) p.set('sort', params.sort)
  if (params.limit) p.set('limit', String(params.limit))
  if (params.offset) p.set('offset', String(params.offset))
  return apiFetch(`/youtube/content?${p}`)
}

export function getYouTubeContentFull(contentId: string): Promise<YouTubeContentFull> {
  return apiFetch(`/youtube/content/${contentId}/full`)
}

export function getYouTubeFormatSplit(): Promise<YouTubeFormatSplit> {
  return apiFetch('/youtube/format-split')
}

export function getYouTubeAudience(): Promise<YouTubeAudience> {
  return apiFetch('/youtube/audience')
}

export function getYouTubeTrafficSources(connectionId: string, days = 30): Promise<{ sources: YouTubeTrafficSource[] }> {
  return apiFetch(`/youtube/traffic-sources?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubeSearchTerms(connectionId: string, days = 30): Promise<YouTubeSearchTerm[]> {
  return apiFetch(`/youtube/search-terms?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubeRetentionOverview(): Promise<YouTubeRetentionOverview> {
  return apiFetch('/youtube/retention-overview')
}

export function getYouTubeLiveTracker(): Promise<YouTubeLiveVideo[]> {
  return apiFetch('/youtube/live-tracker')
}

export function getYouTubeSharing(connectionId: string): Promise<Record<string, number>> {
  return apiFetch(`/youtube/sharing?connectionId=${connectionId}`)
}

// ============================================================
// Trend Intelligence
// ============================================================

export function getTrendingTopics(
  podcastId: string,
): Promise<import('../types/trend-intelligence').TrendingTopicsResponse> {
  return apiFetch(`/trend-intelligence/trending?podcastId=${podcastId}`)
}

// GP_TRENDS_PRIMARY_CORPUS_v1 Phase D — fire-and-forget user-triggered
// resync. Server runs the matcher + tracked-topic gate single-podcast
// scoped in the background and returns 202 immediately. Client typically
// reloads /trending ~30-60s later to pick up the recomputed rows.
export function refreshTrendingTopics(
  podcastId: string,
): Promise<import('../types/trend-intelligence').TrendingRefreshResponse> {
  return apiFetch('/trend-intelligence/refresh', {
    method: 'POST',
    body: JSON.stringify({ podcastId }),
  })
}

export function getGrowthOpportunities(podcastId: string): Promise<GrowthOpportunity[]> {
  return apiFetch(`/trend-intelligence/opportunities?podcastId=${podcastId}`)
}

export function getTrackedTopics(podcastId: string): Promise<SignalTopic[]> {
  return apiFetch(`/trend-intelligence/tracked?podcastId=${podcastId}`)
}

// TI-10: LLM-generated Topic DNA. Returns null if not yet generated (404).
export async function getTopicDna(
  podcastId: string
): Promise<import('../types/trend-intelligence').TopicDna | null> {
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeader()),
  }
  const res = await fetch(
    `${config.apiBase}/trend-intelligence/topic-dna?podcastId=${podcastId}`,
    { headers },
  )
  if (res.status === 404) return null
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new Error(error.message || `API error: ${res.status}`)
  }
  return res.json()
}

// TI-11: User-Defined Topic Interests.
export function getTopicInterests(
  podcastId: string,
): Promise<import('../types/trend-intelligence').TopicInterests> {
  return apiFetch(`/trend-intelligence/topic-interests?podcastId=${podcastId}`)
}

export function putTopicInterests(
  podcastId: string,
  topics: string[],
): Promise<import('../types/trend-intelligence').TopicInterests> {
  return apiFetch(`/trend-intelligence/topic-interests?podcastId=${podcastId}`, {
    method: 'PUT',
    body: JSON.stringify({ topics }),
  })
}

// ============================================================
// Episode DNA
// ============================================================

export function getEpisodeAnalytics(episodeId: string): Promise<EpisodeAnalytics> {
  return apiFetch(`/episodes/${episodeId}/analytics`)
}

export function getEpisodeRetentionCurve(episodeId: string, platform?: string): Promise<RetentionCurveResponse> {
  const qs = platform ? `?platform=${encodeURIComponent(platform)}` : ''
  return apiFetch(`/episodes/${episodeId}/retention-curve${qs}`)
}

export function getEpisodePerformanceWindows(episodeId: string): Promise<EpisodePerformanceWindow[]> {
  return apiFetch(`/episodes/${episodeId}/performance-windows`)
}

export function getShowFollowerTrends(podcastId: string): Promise<ShowFollowerTrend[]> {
  return apiFetch(`/shows/${podcastId}/follower-trends`)
}

export function getShowGeoTrends(podcastId: string, source?: string): Promise<ShowGeoTrend[]> {
  const qs = source ? `?source=${encodeURIComponent(source)}` : ''
  return apiFetch(`/shows/${podcastId}/geo-trends${qs}`)
}

export interface PlatformDemographicsResponse {
  hasData: boolean
  demographics: {
    gender_female_pct: number | null
    gender_male_pct: number | null
    gender_nonbinary_pct: number | null
    age_0_17_pct: number | null
    age_18_22_pct: number | null
    age_23_27_pct: number | null
    age_28_34_pct: number | null
    age_35_44_pct: number | null
    age_45_59_pct: number | null
    age_60_plus_pct: number | null
    geo: Array<{ country: string; pct: number }>
  } | null
  episodeCount: number
}

export function getPlatformDemographics(podcastId: string): Promise<PlatformDemographicsResponse> {
  return apiFetch(`/shows/${podcastId}/platform-demographics`)
}

// ============================================================
// Planned Episodes (Episode Brain — Planning Mode)
// ============================================================

export function getPlannedEpisodes(podcastId: string): Promise<PlannedEpisode[]> {
  return apiFetch(`/planned-episodes?podcastId=${podcastId}`)
}

export function createPlannedEpisode(data: {
  podcast_id: string
  topic: string
  episode_number?: number
  season_number?: number
  roadmap_entry_id?: string
  guest_id?: string
  transcript_type?: string
}): Promise<PlannedEpisode> {
  return apiFetch('/planned-episodes', { method: 'POST', body: JSON.stringify(data) })
}

export function getPlannedEpisode(id: string): Promise<PlannedEpisode & { clip_proposals?: PlannedEpisodeClipProposal[]; guest?: unknown }> {
  return apiFetch(`/planned-episodes/${id}`)
}

export function updatePlannedEpisode(id: string, data: Partial<PlannedEpisode>): Promise<PlannedEpisode> {
  return apiFetch(`/planned-episodes/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deletePlannedEpisode(id: string): Promise<void> {
  return apiFetch(`/planned-episodes/${id}`, { method: 'DELETE' })
}

export function getClipProposals(plannedEpisodeId: string): Promise<PlannedEpisodeClipProposal[]> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/clip-proposals`)
}

export function updateClipProposal(plannedEpisodeId: string, clipId: string, data: { status: string }): Promise<PlannedEpisodeClipProposal> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/clip-proposals/${clipId}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export interface BackCatalogClipResult {
  clipCandidateId: string
  episodeId: string
  episodeTitle: string
  transcriptExcerpt: string
  relevanceScore: number
  relevanceRationale: string
  compositeViralScore: number
  clipType: string
  startSeconds: number
  endSeconds: number
  recommendedPlatforms: string[]
}

export interface TranscriptUploadResult {
  segments_count: number
  speakers_detected: string[]
  source: string
}

export function getBackCatalogClips(plannedEpisodeId: string, limit?: number): Promise<BackCatalogClipResult[]> {
  const qs = limit ? `?limit=${limit}` : ''
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/back-catalog-clips${qs}`)
}

export async function uploadPlannedTranscript(plannedEpisodeId: string, file: File): Promise<TranscriptUploadResult> {
  const formData = new FormData()
  formData.append('file', file)
  const headers = await getAuthHeader()
  const res = await fetch(`${config.apiBase}/planned-episodes/${plannedEpisodeId}/transcript`, {
    method: 'POST',
    headers,
    body: formData,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }))
    throw new Error(err.message)
  }
  return res.json()
}

export function uploadPlannedTranscriptText(plannedEpisodeId: string, text: string): Promise<TranscriptUploadResult> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/transcript`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
}

export function extractClipsFromTranscript(plannedEpisodeId: string): Promise<PlannedEpisodeClipProposal[]> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/extract-clips`, { method: 'POST' })
}

export function generateInkwellTitles(data: { planned_episode_id: string; topic: string; podcast_id: string }): Promise<unknown[]> {
  return apiFetch('/inkwell/generate-titles', { method: 'POST', body: JSON.stringify(data) })
}

export function generateTopicBrief(data: { planned_episode_id: string; topic: string; podcast_id: string }): Promise<{
  hook_lines: { text: string; energy_match_score: number }[]
  framing: string
  seo_keywords: string[]
  audience_desc: string
  thumbnail_concept: string
}> {
  return apiFetch('/inkwell/topic-brief', { method: 'POST', body: JSON.stringify(data) })
}

// ── Recorded Mode: Guest Detection ──

export interface GuestDetectionResult {
  guests: { name: string; confidence: number; evidence: string }[]
  hostIdentified: string | null
  matchedGuestId: string | null
  candidateGuestName: string | null
  candidateGuestId: string | null
  matchType: 'auto' | 'candidate' | 'not_found' | 'no_guest'
}

export function detectGuest(plannedEpisodeId: string): Promise<GuestDetectionResult> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/detect-guest`, { method: 'POST' })
}

// ── Recorded Mode: Teaser Generation + Refinement ──

export interface TeaserSegment {
  order: number
  startSeconds: number
  endSeconds: number
  excerpt: string
  rationale: string
}

export function generateTeaser(plannedEpisodeId: string): Promise<{ message: string }> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/generate-teaser`, { method: 'POST' })
}

export function refineTeaser(
  plannedEpisodeId: string,
  data: { user_instruction: string; current_segments: TeaserSegment[] }
): Promise<{ segments: TeaserSegment[]; refinement_summary: string }> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/refine-teaser`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ── Recorded Mode: Episode Description ──

export function generateEpisodeDescription(plannedEpisodeId: string): Promise<{
  short_description: string
  long_description_md: string
}> {
  return apiFetch('/inkwell/generate-description', {
    method: 'POST',
    body: JSON.stringify({ planned_episode_id: plannedEpisodeId }),
  })
}

// ── Recorded Mode: Audio Upload ──

export async function uploadPlannedAudio(plannedEpisodeId: string, file: File): Promise<{
  audio_url: string
  audio_duration_seconds: number | null
  transcription_started: boolean
}> {
  const headers = await getAuthHeader()
  const formData = new FormData()
  formData.append('audio', file)

  const res = await fetch(`${config.apiBase}/planned-episodes/${plannedEpisodeId}/audio`, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new Error(error.message || `Upload failed: ${res.status}`)
  }

  return res.json()
}

// ── Signed Audio URL ──

export async function getAudioSignedUrl(plannedEpisodeId: string): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(`/planned-episodes/${plannedEpisodeId}/audio-url`)
}

export async function getTeaserSignedUrl(plannedEpisodeId: string): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(`/planned-episodes/${plannedEpisodeId}/teaser-audio-url`)
}

// ── Descript ──

export function sendClipToDescript(data: {
  clip_proposal_id: string
  source_media_url: string
  caption_style?: string
  apply_studio_sound?: boolean
}): Promise<DescriptClipJob> {
  return apiFetch('/descript/send-clip', { method: 'POST', body: JSON.stringify(data) })
}

export function getDescriptJobStatus(jobId: string): Promise<DescriptClipJob> {
  return apiFetch(`/descript/jobs/${jobId}/status`)
}

export async function uploadRenderedClip(jobId: string, file: File): Promise<DescriptClipJob> {
  const headers = await getAuthHeader()
  const formData = new FormData()
  formData.append('clip', file)
  const res = await fetch(`${config.apiBase}/descript/jobs/${jobId}/upload-rendered`, {
    method: 'POST',
    headers,
    body: formData,
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new Error(error.message || `Upload failed: ${res.status}`)
  }
  return res.json()
}

// ── Descript Connection ──

export function connectDescript(params: { api_token: string; drive_id: string; podcast_id: string }): Promise<{ connected: boolean }> {
  return apiFetch('/descript/connect', { method: 'POST', body: JSON.stringify(params) })
}

export function getDescriptConnectionStatus(): Promise<{ connected: boolean; drive_id?: string }> {
  return apiFetch('/descript/connection-status')
}

export function disconnectDescript(): Promise<{ disconnected: boolean }> {
  return apiFetch('/descript/disconnect', { method: 'DELETE' })
}

// ── Speaker Resolution ──

export function resolveSpeakers(
  plannedEpisodeId: string,
  data: { speaker_name_map: Record<string, string>; speaker_role_map: Record<string, 'host' | 'guest'> }
): Promise<{ speaker_name_map: Record<string, string>; speaker_role_map: Record<string, 'host' | 'guest'>; transcript_draft_segments: unknown[] }> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/resolve-speakers`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ── Teaser Export ──

export function exportTeaserMp3(plannedEpisodeId: string): Promise<{
  teaser_url: string
  duration_seconds: number
  transcript_text: string
}> {
  return apiFetch(`/planned-episodes/${plannedEpisodeId}/export-teaser`, { method: 'POST' })
}

// ============================================================
// YouTube Untapped Intelligence (Phase 15)
// ============================================================

export interface YouTubeCardRow {
  cardType: string | null
  cardId: string | null
  impressions: number
  teaserImpressions: number
  clicks: number
  teaserClicks: number
  clickRate: number
  teaserClickRate: number
}

export interface YouTubeEndScreenRow {
  elementType: string | null
  elementId: string | null
  impressions: number
  clicks: number
  clickRate: number
}

export interface YouTubeReachRow {
  trafficSourceType: string
  impressions: number
  ctr: number
}

export function getYouTubeCards(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubeCardRow[] }> {
  return apiFetch(`/youtube/cards?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubeEndScreens(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubeEndScreenRow[] }> {
  return apiFetch(`/youtube/end-screens?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubeReachByTraffic(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubeReachRow[] }> {
  return apiFetch(`/youtube/reach-by-traffic?connectionId=${connectionId}&days=${days}`)
}

// ── Batch B: Playback Location + Sharing Service + Subtitles ──

export interface YouTubePlaybackRow {
  locationType: string
  views: number
  watchTimeMinutes: number
}

export interface YouTubeSharingRow {
  sharingService: string
  shares: number
}

export interface YouTubeSubtitleRow {
  language: string
  autotranslated: boolean
  views: number
  watchTimeMinutes: number
}

export function getYouTubePlaybackLocations(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubePlaybackRow[] }> {
  return apiFetch(`/youtube/playback-locations?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubeSharingServices(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubeSharingRow[] }> {
  return apiFetch(`/youtube/sharing-services?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubeSubtitles(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubeSubtitleRow[] }> {
  return apiFetch(`/youtube/subtitles?connectionId=${connectionId}&days=${days}`)
}

// ── Batch C: Combined cross-dim matrix + US Province ──

export interface YouTubeCombinedCell {
  row: string
  col: string
  value: number
}

export interface YouTubeCombinedBreakdown {
  matrix: YouTubeCombinedCell[]
  rows: string[]
  cols: string[]
}

export interface YouTubeProvinceRow {
  provinceCode: string
  views: number
  watchTimeMinutes: number
}

export function getYouTubeCombinedBreakdown(
  connectionId: string,
  days = 30,
): Promise<YouTubeCombinedBreakdown> {
  return apiFetch(`/youtube/combined-breakdown?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubeUsProvinces(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubeProvinceRow[] }> {
  return apiFetch(`/youtube/us-provinces?connectionId=${connectionId}&days=${days}`)
}

// ============================================================
// YouTube Playlist Intelligence (Phase 16)
// ============================================================

export interface YouTubePlaylistRow {
  playlistId: string
  title: string | null
  description: string | null
  videoCount: number | null
}

export interface YouTubePlaylistPerformanceRow {
  playlistId: string
  title: string | null
  videoCount: number | null
  views: number
  watchTimeMinutes: number
  avgViewDurationSeconds: number
  saves: number
  starts: number
}

export interface YouTubePlaylistTrafficRow {
  playlistId: string
  title: string | null
  breakdown: Array<{ trafficSourceType: string; views: number }>
}

export function getYouTubePlaylists(
  connectionId: string,
): Promise<{ items: YouTubePlaylistRow[] }> {
  return apiFetch(`/youtube/playlists?connectionId=${connectionId}`)
}

export function getYouTubePlaylistPerformance(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubePlaylistPerformanceRow[] }> {
  return apiFetch(`/youtube/playlist-performance?connectionId=${connectionId}&days=${days}`)
}

export function getYouTubePlaylistTrafficBreakdown(
  connectionId: string,
  days = 30,
): Promise<{ items: YouTubePlaylistTrafficRow[] }> {
  return apiFetch(`/youtube/playlist-traffic?connectionId=${connectionId}&days=${days}`)
}

// ============================================================
// Team Members (Phase 4/5) — public invite endpoints
// These call public routes (no auth); backend responds with
// `{ error: "<code>" }` on failures. Use raw fetch so the error
// code string survives to the caller for user-facing message
// mapping (apiFetch only reads the `message` key).
// ============================================================

export class TeamInviteError extends Error {
  readonly code: string
  readonly status: number
  constructor(code: string, status: number) {
    super(code)
    this.code = code
    this.status = status
  }
}

async function teamPublicFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${config.apiBase}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const code =
      typeof body?.error === 'string' ? body.error : `http_${res.status}`
    throw new TeamInviteError(code, res.status)
  }
  return res.json() as Promise<T>
}

export function teamValidateInvite(token: string): Promise<{
  email: string
  adminDisplayName: string | null
  expiresAt: string
}> {
  return teamPublicFetch(
    `/team/validate-invite?token=${encodeURIComponent(token)}`,
  )
}

export function teamAccept(body: {
  token: string
  password: string
  displayName?: string
}): Promise<{ email: string }> {
  return teamPublicFetch('/team/accept', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// Admin team endpoints (authenticated). Like apiFetch but parses
// `{error: "code"}` error body into TeamInviteError for caller mapping.
async function teamAdminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${config.apiBase}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(await getAuthHeader()),
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const code =
      typeof body?.error === 'string' ? body.error : `http_${res.status}`
    throw new TeamInviteError(code, res.status)
  }
  return res.json() as Promise<T>
}

type TeamMemberListResponse = {
  members: Array<{
    id: string
    admin_user_id: string
    member_user_id: string | null
    email: string
    display_name: string | null
    status: 'pending' | 'active' | 'suspended' | 'removed'
    permissions: Record<string, boolean>
    invite_token: string | null
    invite_expires_at: string | null
    invited_at: string
    accepted_at: string | null
    last_active_at: string | null
    created_at: string
    updated_at: string
  }>
  seatsUsed: number
  seatCap: number
}

type TeamInviteResponse = {
  id: string
  email: string
  status: string
  inviteUrl: string
  expiresAt: string | null
  emailSent: boolean
  emailError: string | null
}

export function teamListMembers(): Promise<TeamMemberListResponse> {
  return teamAdminFetch('/team')
}

export function teamInvite(body: {
  email: string
  displayName?: string
  permissions?: Record<string, boolean>
}): Promise<TeamInviteResponse> {
  return teamAdminFetch('/team/invite', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function teamUpdate(
  id: string,
  patch: {
    permissions?: Record<string, boolean>
    status?: 'active' | 'suspended' | 'removed'
    displayName?: string | null
  },
): Promise<TeamMemberListResponse['members'][number]> {
  return teamAdminFetch(`/team/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function teamRemove(id: string): Promise<{ ok: true }> {
  return teamAdminFetch(`/team/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function teamResendInvite(id: string): Promise<TeamInviteResponse> {
  return teamAdminFetch(`/team/${encodeURIComponent(id)}/resend`, {
    method: 'POST',
  })
}
